<script setup>
import { computed } from "vue";

const props = defineProps({
  show: Boolean,
  status: { type: String, default: "loading" }, // loading, success, error, info
  title: { type: String, default: "" },
  message: { type: String, default: "" },
});

const emit = defineEmits(["close"]);

function close() {
  if (props.status !== 'loading') {
    emit("close");
  }
}

const iconName = computed(() => {
  switch (props.status) {
    case 'success': return 'check_circle';
    case 'error': return 'error';
    case 'info': return 'info';
    default: return '';
  }
});

const iconClass = computed(() => {
  switch (props.status) {
    case 'success': return 'success-wrapper';
    case 'error': return 'error-wrapper';
    case 'info': return 'info-wrapper';
    default: return '';
  }
});
</script>

<template>
  <Transition name="modal">
    <div v-if="show" class="modal-overlay" @click.self="close">
      <div class="modal-container">
        <div class="modal-content">
          
          <!-- Loading State -->
          <div v-if="status === 'loading'" class="loading-wrapper">
            <div class="spinner"></div>
          </div>

          <!-- Result State -->
          <div v-else class="icon-wrapper" :class="iconClass">
            <span class="material-symbols-rounded icon">{{ iconName }}</span>
          </div>

          <h2 class="modal-title" :class="status">{{ title }}</h2>
          <p v-if="message" class="modal-message">{{ message }}</p>
          
          <button v-if="status !== 'loading'" class="close-btn" :class="status" @click="close">
            Entendido
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
/* ===== MODAL OVERLAY ===== */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 1rem;
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* ===== MODAL CONTAINER ===== */
.modal-container {
  position: relative;
  background: var(--surface);
  border: 1px solid rgba(124, 92, 255, 0.2);
  border-radius: 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4),
    0 0 0 1px rgba(255, 255, 255, 0.05) inset;
  width: 100%;
  max-width: 420px;
  overflow: hidden;
  animation: slideUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  color: var(--text);
  backdrop-filter: blur(20px);
}

@keyframes slideUp {
  from { opacity: 0; transform: translateY(40px) scale(0.95); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}

/* ===== MODAL CONTENT ===== */
.modal-content {
  padding: 2.5rem 2rem;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.25rem;
}

/* ===== ICONS & SPINNER ===== */
.loading-wrapper {
  padding: 1rem;
}

.spinner {
  width: 60px;
  height: 60px;
  border: 4px solid rgba(124, 92, 255, 0.1);
  border-top-color: #7c5cff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.icon-wrapper {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: scaleIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
}

.success-wrapper {
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  box-shadow: 0 8px 24px rgba(34, 197, 94, 0.3);
}

.error-wrapper {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  box-shadow: 0 8px 24px rgba(239, 68, 68, 0.3);
}

.info-wrapper {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  box-shadow: 0 8px 24px rgba(59, 130, 246, 0.3);
}

@keyframes scaleIn {
  from { transform: scale(0); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}

.icon {
  font-size: 3rem;
  color: #fff;
  animation: checkmark 0.6s ease-in-out 0.2s backwards;
}

@keyframes checkmark {
  0% { transform: scale(0) rotate(-45deg); opacity: 0; }
  50% { transform: scale(1.2) rotate(0deg); }
  100% { transform: scale(1) rotate(0deg); opacity: 1; }
}

/* ===== TEXT ===== */
.modal-title {
  font-size: 1.5rem;
  font-weight: 800;
  margin: 0;
  letter-spacing: -0.01em;
}

.modal-title.loading { color: var(--text); }
.modal-title.success { color: #22c55e; }
.modal-title.error { color: #ef4444; }
.modal-title.info { color: #3b82f6; }

.modal-message {
  color: var(--muted);
  font-size: 1rem;
  margin: 0;
  line-height: 1.6;
}

/* ===== BUTTON ===== */
.close-btn {
  margin-top: 0.5rem;
  padding: 0.875rem 2rem;
  border: none;
  border-radius: 12px;
  color: #fff;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  min-width: 140px;
}

.close-btn.success {
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  box-shadow: 0 4px 16px rgba(34, 197, 94, 0.3);
}

.close-btn.error {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  box-shadow: 0 4px 16px rgba(239, 68, 68, 0.3);
}

.close-btn.info {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  box-shadow: 0 4px 16px rgba(59, 130, 246, 0.3);
}

.close-btn:hover {
  transform: translateY(-2px);
  filter: brightness(1.1);
}

.close-btn:active {
  transform: translateY(0);
}

/* ===== TRANSITIONS ===== */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .modal-container,
.modal-leave-active .modal-container {
  transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.modal-enter-from .modal-container,
.modal-leave-to .modal-container {
  transform: scale(0.9) translateY(20px);
  opacity: 0;
}
</style>
